import React from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import { EmployeePersonalityCommunicationStyle } from 'src/api/generated/models/EmployeePersonalityCommunicationStyle';
import { Card } from 'src/modules/shared/components/atoms/cards';
import Tag from 'src/modules/shared/components/atoms/tag';

interface CommunicationStyleOption {
  value: EmployeePersonalityCommunicationStyle;
  title: string;
  description: string;
  traits: string[];
}

interface CommunicationStyleSelectorProps {
  value: EmployeePersonalityCommunicationStyle;
  onChange: (value: EmployeePersonalityCommunicationStyle) => void;
}

// TODO: check when implementing i18n
const communicationStyles: CommunicationStyleOption[] = [
  {
    value: EmployeePersonalityCommunicationStyle.PROFESSIONAL,
    title: 'Profesional',
    description:
      'Tono formal y cortés, con lenguaje neutral. Ideal para comunicaciones corporativas que requieren formalidad.',
    traits: ['Solución-orientado', 'Analítico', 'Preciso', 'Confiable'],
  },
  {
    value: EmployeePersonalityCommunicationStyle.FRIENDLY,
    title: 'Amigable',
    description:
      'Tono cálido y cercano que genera confianza. Perfecto para atención al cliente, onboarding y relaciones con usuarios.',
    traits: ['Empático', 'Paciente', 'Comprensivo', 'Positivo'],
  },
  {
    value: EmployeePersonalityCommunicationStyle.ASSERTIVE,
    title: 'Asertivo',
    description:
      'Comunicación directa y orientada a resultados. Efectivo para liderar equipos, negociaciones y situaciones que requieren acción inmediata.',
    traits: ['Orientado a objetivos', 'Persuasivo', 'Decisivo', 'Estratégico'],
  },
  {
    value: EmployeePersonalityCommunicationStyle.TECHNICAL,
    title: 'Técnico',
    description:
      'Lenguaje especializado y basado en datos. Ideal para documentación técnica, soporte especializado y capacitación avanzada.',
    traits: ['Metódico', 'Minucioso', 'Sistemático', 'Lógico'],
  },
];

export const CommunicationStyleSelector: React.FC<
  CommunicationStyleSelectorProps
> = ({ value, onChange }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {communicationStyles.map((style) => {
        const isSelected = value === style.value;
        return (
          <Card
            key={style.value}
            onClick={() => onChange(style.value)}
            isHighlighted={isSelected}
            shadow="none"
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            variant="info"
            appearance="outline"
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Text size="paragraph" weight="medium">
                  {style.title}
                </Text>

                <Text size="tiny" color="secondary">
                  {style.description}
                </Text>
              </div>

              <div className="flex flex-wrap gap-2">
                {style.traits.map((trait) => (
                  <Tag
                    key={trait}
                    content={trait}
                    size="sm"
                    variant="info"
                    appearance={isSelected ? 'soft' : 'filled'}
                  />
                ))}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
